import {React, useEffect, useState} from 'react';
import { Box, Card, Flex, Text, Inset, IconButton, Dialog, Button } from "@radix-ui/themes";
import { useStore } from '../store/store';
import { useNavigate } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiPlus, mdiDelete, mdiFeather, mdiBookOpen, mdiChevronRight } from '@mdi/js';
import api from '../services/api';
import avatar from '../assets/avatar3.png';
import { initiateOpenRouterAuth, API_KEY_UPDATED_EVENT } from '../components/SettingsScreen';

export const Dashboard = () => {
    const [state, actions] = useStore();
    const navigate = useNavigate();
    const [hoveredNovel, setHoveredNovel] = useState(null);
    const [novelToDelete, setNovelToDelete] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [hasApiKey, setHasApiKey] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);

    useEffect(() => {
        const loggedInUser = localStorage.getItem('user');
        const apiKey = localStorage.getItem('openRouterApiKey');
        setHasApiKey(!!apiKey);
    
        if (loggedInUser) {
            const userData = JSON.parse(loggedInUser);
            actions.setUser(userData);
        }

        // Handle callback from OAuth process
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        
        if (code) {
            handleCodeExchange(code);
        }
    }, []);

    // Exchange code for API key
    const handleCodeExchange = async (code) => {
        try {
            setIsConnecting(true);
            
            // Retrieve code_verifier from sessionStorage
            const codeVerifier = sessionStorage.getItem('openrouter_code_verifier');
            const codeMethod = sessionStorage.getItem('openrouter_code_method');
            
            if (!codeVerifier) {
                throw new Error('Code verifier not found. Please try connecting again.');
            }

            const response = await fetch('https://openrouter.ai/api/v1/auth/keys', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    code: code,
                    code_verifier: codeVerifier,
                    code_challenge_method: codeMethod || 'S256',
                }),
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to get API key: ${response.status} ${errorText}`);
            }
            
            const { key } = await response.json();
            console.log('Received OpenRouter API key:', key);
            
            // Store the API key
            localStorage.setItem('openRouterApiKey', key);
            setHasApiKey(true);
            
            // Notify the application that the API key has been updated
            window.dispatchEvent(new CustomEvent(API_KEY_UPDATED_EVENT, { detail: { key } }));
            
            // Clean up
            sessionStorage.removeItem('openrouter_code_verifier');
            sessionStorage.removeItem('openrouter_code_method');
            
            // Remove code from URL
            const url = new URL(window.location.href);
            url.searchParams.delete('code');
            window.history.replaceState({}, document.title, url.toString());
            
        } catch (error) {
            console.error('Error exchanging code for API key:', error);
            alert(`Failed to connect to OpenRouter: ${error.message}`);
        } finally {
            setIsConnecting(false);
        }
    };

    const openDeleteDialog = (e, novelId) => {
        e.stopPropagation(); // Prevent navigation to the novel page
        setNovelToDelete(novelId);
        setDeleteDialogOpen(true);
    };

    const handleDeleteNovel = async () => {
        try {
            const response = await api.deleteNovel(novelToDelete);
            if (response.success) {
                // Update the novels list in the store
                const updatedNovels = state.novels.filter(novel => novel.id !== novelToDelete);
                actions.setNovels(updatedNovels);
            } else {
                alert('Failed to delete novel: ' + (response.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error deleting novel:', error);
            alert('An error occurred while deleting the novel');
        } finally {
            setDeleteDialogOpen(false);
            setNovelToDelete(null);
        }
    };

    const handleConnectOpenRouter = () => {
        initiateOpenRouterAuth();
    };

    const novels = state.novels.map(novel => {
        return (
            <Box className="novel-card-container" key={novel.id}>
                <Card 
                    className='dashboard-novel-card-modern' 
                    onClick={() => navigate(`/novel/${novel.id}`)}
                    onMouseEnter={() => setHoveredNovel(novel.id)}
                    onMouseLeave={() => setHoveredNovel(null)}
                >
                    <div className="novel-card-content">
                        
                        <h3 className="novel-card-title">{novel.title}</h3>
                        
                        <div className="novel-card-stats">
                            <div className="stat-item">
                                <span className="stat-value">{novel.word_count.toLocaleString()}</span>
                                <span className="stat-label">words</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">{novel.reading_time_mins}</span>
                                <span className="stat-label">mins</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">${novel.cost.toFixed(2)}</span>
                                <span className="stat-label">cost</span>
                            </div>
                        </div>
                        
                        <div className="novel-card-actions">
                            <div className="open-indicator">
                                <span>Open</span>
                                <Icon path={mdiChevronRight} size={0.7} />
                            </div>
                            
                            {!state.readOnly && (
                                <IconButton 
                                    className={`novel-delete-button ${hoveredNovel === novel.id ? 'visible' : ''}`}
                                    color="red" 
                                    variant="solid" 
                                    size="1"
                                    onClick={(e) => openDeleteDialog(e, novel.id)}
                                >
                                    <Icon path={mdiDelete} size={0.7} />
                                </IconButton>
                            )}
                        </div>
                    </div>
                </Card>
            </Box>
        );
    });

    const newNovel = (
        <Box className="novel-card-container">
            <Card className='dashboard-novel-card-modern new-novel' onClick={() => navigate('/novel/new')}>
                <div className="novel-card-content">
                    <div className="novel-card-icon new">
                        <Icon path={mdiFeather} size={1.2} />
                    </div>
                    <h3 className="novel-card-title">Create New Novel</h3>
                    <div className="novel-card-new-indicator">
                        <Icon path={mdiPlus} size={1} />
                    </div>
                </div>
            </Card>
        </Box>
    );

      // If no API key, show prompt to connect
        if (!hasApiKey) {
            return (
            <div className="home-screen">   
                <Flex className={"tutorial-text"} style={{ margin: '0 auto', width: '66%', marginBottom: '-60px' }}>
                    <img src={avatar} alt="Avatar" className="tutorial-avatar" />
                    <div>Thank You for trying out PlotGoblin! To get started, connect to your OpenRouter account, using the button below!</div>
                </Flex>

                <div className="api-connection-prompt">
                <h2>Connect to OpenRouter</h2>
                <div className="api-connection-icon">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z" stroke="#4b0082" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 2C13.3132 2 14.6136 2.25866 15.8268 2.7612C17.0401 3.26375 18.1425 4.00035 19.0711 4.92893C19.9997 5.85752 20.7362 6.95991 21.2388 8.17317C21.7413 9.38642 22 10.6868 22 12C22 14.6522 20.9464 17.1957 19.0711 19.0711C17.1957 20.9464 14.6522 22 12 22C10.6868 22 9.38642 21.7413 8.17317 21.2388C6.95991 20.7362 5.85752 19.9997 4.92893 19.0711C3.05357 17.1957 2 14.6522 2 12C2 9.34784 3.05357 6.8043 4.92893 4.92893C6.8043 3.05357 9.34784 2 12 2Z" stroke="#4b0082" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 12H22" stroke="#4b0082" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
                <p>To start creating stories with PlotGoblin, you need to connect to OpenRouter first.</p>
                <p>OpenRouter provides access to powerful AI models that power PlotGoblin's creative features.</p>
                {isConnecting ? (
                    <button className="connect-api-button loading" disabled>
                        <span className="loading-spinner"></span>
                        Connecting...
                    </button>
                ) : (
                    <button className="connect-api-button" onClick={handleConnectOpenRouter}>
                        Connect to OpenRouter
                    </button>
                )}
                </div>
            </div>
            );
        }

    return (
        <div style={{ 
            padding: '15px 20px', 
            margin: '0 auto',
            width: '66%', 
            }}>
            <Text as='div' className='dashboard-header' align={'left'}>Your Novels</Text>

            {state.novels.length === 0 ?
            <Flex className="tutorial-container" style={{ }}>
                <Flex className={"tutorial-text"}>
                    <img src={avatar} alt="Avatar" className="tutorial-avatar" />
                    <div>Amazing! I'll guide you through creating your first book! Let's start by clicking 'Create New Novel' below!</div>
                </Flex>
            </Flex> : null}

            <Flex gap="20px" wrap={"wrap"}>
                {!state.readOnly &&
                    <>{newNovel}</>
                }
                {novels}
            </Flex>

            {/* Delete Confirmation Dialog */}
            <Dialog.Root open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <Dialog.Content style={{ maxWidth: 450 }}>
                    <Dialog.Title>Delete Novel</Dialog.Title>
                    <Dialog.Description size="2" mb="4">
                        Are you sure you want to delete this novel? This action cannot be undone.
                    </Dialog.Description>

                    <Flex gap="3" mt="4" justify="end">
                        <Dialog.Close>
                            <Button variant="soft" color="gray">
                                Cancel
                            </Button>
                        </Dialog.Close>
                        <Button variant="solid" color="red" onClick={handleDeleteNovel}>
                            Delete
                        </Button>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>
        </div>
    );
};

export default Dashboard;